import React, { useState } from 'react';
import { CalendarClock, CheckCircle, XCircle } from 'lucide-react';
import Button from '../../shared/Button';
import {
  useApproveEventDateChange,
  useRejectEventDateChange,
} from '../../../services/bookingDateChangeService';

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const EventDateChangeReviewPanel = ({ bookingId, changeRequest, clientName, onResolved }) => {
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [error, setError] = useState('');

  const approveMutation = useApproveEventDateChange({
    onSuccess: () => {
      setError('');
      setConfirmApprove(false);
      onResolved?.();
    },
    onError: (err) => {
      setError(err?.response?.data?.message || 'Failed to approve request');
    },
  });

  const rejectMutation = useRejectEventDateChange({
    onSuccess: () => {
      setShowRejectForm(false);
      setRejectionReason('');
      setError('');
      onResolved?.();
    },
    onError: (err) => {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.errors?.rejection_reason?.[0] ||
          'Failed to decline request'
      );
    },
  });

  if (!changeRequest || changeRequest.status !== 'pending') {
    return null;
  }

  const handleApprove = () => {
    approveMutation.mutate({ bookingId });
  };

  const handleReject = (e) => {
    e.preventDefault();
    if (rejectionReason.trim().length < 3) {
      setError('Please include a brief reason (at least 3 characters).');
      return;
    }
    rejectMutation.mutate({ bookingId, rejection_reason: rejectionReason.trim() });
  };

  const busy = approveMutation.isPending || rejectMutation.isPending;

  return (
    <div className="bg-white border border-amber-200 rounded-xl overflow-hidden shadow-sm">
      <div className="bg-amber-50 px-4 py-3 border-b border-amber-200 flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
          <CalendarClock className="w-5 h-5 text-amber-800" />
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">
            Pending your review
          </p>
          <h3 className="text-sm font-semibold text-gray-900">Event date change request</h3>
          <p className="text-xs text-gray-600 mt-0.5">
            {clientName || 'The client'} asked to move this booking. Approving updates the confirmed event date.
          </p>
        </div>
      </div>

      <div className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-4">
          <div className="rounded-lg p-3 border border-gray-200 bg-gray-50">
            <p className="text-[11px] uppercase tracking-wide font-medium text-gray-500">Current date</p>
            <p className="font-semibold text-gray-900 mt-1">
              {formatDate(changeRequest.previous_event_date)}
            </p>
          </div>
          <div className="rounded-lg p-3 border border-indigo-200 bg-indigo-50">
            <p className="text-[11px] uppercase tracking-wide font-medium text-indigo-700">Requested date</p>
            <p className="font-semibold text-indigo-950 mt-1">
              {formatDate(changeRequest.requested_event_date)}
            </p>
          </div>
        </div>

        {changeRequest.client_note && (
          <div className="mb-4 text-sm">
            <p className="text-[11px] uppercase tracking-wide font-medium text-gray-500 mb-1">
              Message from client
            </p>
            <p className="text-gray-800 bg-gray-50 rounded-lg p-3 border border-gray-200 leading-relaxed">
              {changeRequest.client_note}
            </p>
          </div>
        )}

        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
            {error}
          </p>
        )}

        {!showRejectForm && !confirmApprove && (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              icon={CheckCircle}
              onClick={() => setConfirmApprove(true)}
              disabled={busy}
            >
              Approve new date
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              icon={XCircle}
              onClick={() => setShowRejectForm(true)}
              disabled={busy}
            >
              Decline request
            </Button>
          </div>
        )}

        {confirmApprove && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-3">
            <p className="text-sm text-emerald-950">
              Confirm this booking for{' '}
              <strong>{formatDate(changeRequest.requested_event_date)}</strong>? The client will be emailed the
              updated date.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                icon={CheckCircle}
                onClick={handleApprove}
                loading={approveMutation.isPending}
                disabled={busy}
              >
                Confirm & update booking
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setConfirmApprove(false)}
                disabled={busy}
              >
                Back
              </Button>
            </div>
          </div>
        )}

        {showRejectForm && (
          <form onSubmit={handleReject} className="space-y-3 rounded-lg border border-gray-200 p-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Reason for the client *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder="Let the client know why this date cannot be accommodated..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="submit"
                size="sm"
                variant="danger"
                loading={rejectMutation.isPending}
                disabled={busy}
              >
                Send decline
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowRejectForm(false);
                  setRejectionReason('');
                  setError('');
                }}
                disabled={busy}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EventDateChangeReviewPanel;
